import React from 'react';
import { useParams, Link } from 'react-router-dom';
import './ProgramDetail.css'

const ProgramDetail = () => {
  const { slug } = useParams();

  // Program data (you might want to move this to a separate data file)
  const programs = {
    "innovation-lab": {
      id: 1,
      title: "ClinAddis Innovation Lab",
      partner: "Ministry of Health",
      description: "A collaborative platform designed to develop, test, and scale innovative healthcare solutions that directly address Ethiopia's most pressing health system challenges.",
      detailedDescription: `
        The ClinAddis Innovation Lab is a collaborative platform designed to develop, test, and scale
        innovative healthcare solutions that directly address Ethiopia's most pressing health system
        challenges. Working closely with the Ministry of Health and key stakeholders, the lab bridges the
        gap between ideas and real-world implementation.
      `,
      whyItMatters: `
        Ethiopia's healthcare system faces challenges related to access, efficiency, workforce capacity,
        and technology adoption. Many promising ideas fail to move beyond concept stages. The
        Innovation Lab exists to turn local ideas into practical, scalable solutions that work within
        Ethiopia's context.
      `,
      whatWeDo: [
        "Facilitate innovation sprints and problem-solving workshops",
        "Support early-stage health innovations and pilot projects",
        "Connect innovators with policymakers, clinicians, and technical experts",
        "Promote evidence-based and context-appropriate solutions"
      ],
      targetAudience: [
        "Health professionals and clinicians",
        "Health science students and researchers",
        "Digital health innovators and entrepreneurs",
        "Policymakers and program implementers"
      ],
      outcomes: [
        "Prototype healthcare solutions tested in real settings",
        "Policy-relevant insights and recommendations",
        "Strengthened innovation capacity within the health sector"
      ],
      icon: "🔬",
      images: ["/lab1.jpg", "/lab2.jpg"],
    },

    "skills-leadership": {
      id: 2,
      title: "ClinAddis Skills & Leadership Program",
      partner: "U.S. Embassy",
      description: "A comprehensive capacity-building initiative focused on developing the next generation of healthcare leaders through leadership training, technical skills, and professional development.",
      detailedDescription: `
        The ClinAddis Skills & Leadership Program is a comprehensive capacity-building initiative
        focused on developing the next generation of healthcare leaders. The program combines
        leadership training, technical skills, and professional development tailored for healthcare
        professionals and students.
      `,
      whyItMatters: `
        Technical knowledge alone is not enough to transform healthcare. Strong leadership,
        communication, ethics, and systems thinking are critical. This program prepares participants to
        lead effectively within clinical, academic, and policy environments.
      `,
      coreFocusAreas: [
        "Leadership and decision-making in healthcare",
        "Health communication and advocacy",
        "Professional development and career readiness",
        "Ethics, teamwork, and systems thinking"
      ],
      learningApproach: [
        "Interactive workshops and seminars",
        "Case-based discussions",
        "Guest lectures from local and international experts",
        "Mentorship and peer learning"
      ],
      targetAudience: [
        "Medical and health science students",
        "Early- to mid-career healthcare professionals",
        "Individuals interested in leadership and policy roles"
      ],
      outcomes: [
        "Improved leadership and communication skills",
        "Stronger professional networks",
        "Increased readiness for leadership and global opportunities"
      ],
      icon: "🎯",
    },

    "art-medicine": {
      id: 3,
      title: "Art & Medicine",
      partner: "Kikundi",
      description: "A unique interdisciplinary program exploring the intersection of healthcare, art, and human experience.",
      detailedDescription: `
        Art & Medicine is a unique interdisciplinary program exploring the intersection of healthcare,
        art, and human experience. It uses artistic expression as a tool for healing, reflection,
        communication, and medical education.
      `,
      whyItMatters: `
        Healthcare is deeply human. Art helps clinicians and students understand empathy, patient
        narratives, burnout, and emotional resilience. This program creates space for reflection beyond
        traditional medical training.
      `,
      programElements: [
        "Art exhibitions and curated discussions",
        "Interactive workshops with artists and clinicians",
        "Storytelling, visual art, and creative expression",
        "Dialogue on mental health, empathy, and healing"
      ],
      targetAudience: [
        "Healthcare professionals and students",
        "Artists interested in health and social impact",
        "Mental health advocates and educators"
      ],
      impact: [
        "Improved empathy and patient-centered thinking",
        "Increased awareness of mental health in healthcare",
        "Stronger connections between medicine and the community"
      ],
      icon: "🎨",
    },

    "community-connect": {
      id: 4,
      title: "ClinAddis Community Connect",
      partner: "Chaka Coffee",
      description: "A community engagement initiative that brings healthcare conversations into public spaces to promote health awareness, dialogue, and trust between healthcare professionals and the public.",
      detailedDescription: `
        ClinAddis Community Connect is a community engagement initiative that brings healthcare
        conversations into public spaces. By hosting discussions in accessible venues, the program
        promotes health awareness, dialogue, and trust between healthcare professionals and the
        public.
      `,
      whyItMatters: `
        Health education should not be limited to hospitals or classrooms. Community-based
        conversations empower people to ask questions, share experiences, and make informed
        decisions about their health.
      `,
      whatHappens: [
        "Public health talks and open discussions",
        "Community Q&A sessions with health professionals",
        "Awareness campaigns on key health topics",
        "Informal, inclusive learning environments"
      ],
      targetAudience: [
        "General public",
        "Youth and community groups",
        "Health professionals interested in outreach"
      ],
      outcomes: [
        "Increased health awareness",
        "Stronger community–health system relationships",
        "Improved public trust and engagement"
      ],
      icon: "🤝",
    },

    "public-health-forum": {
      id: 5,
      title: "ClinAddis Public Health Forum",
      partner: "Africa CDC",
      description: "A platform for high-level discussion on critical public health issues affecting Ethiopia and Africa, bringing together professionals, policymakers, researchers, and students.",
      detailedDescription: `
        The ClinAddis Public Health Forum is a platform for high-level discussion on critical public
        health issues affecting Ethiopia and Africa. It brings together professionals, policymakers,
        researchers, and students to explore solutions through dialogue and collaboration.
      `,
      whyItMatters: `
        Public health challenges require multi-sectoral collaboration. This forum encourages
        evidence-based discussion and shared problem-solving for sustainable health outcomes.
      `,
      focusAreas: [
        "Health policy and systems strengthening",
        "Disease prevention and preparedness",
        "Health equity and population health",
        "Regional and continental health challenges"
      ],
      format: [
        "Panel discussions and forums",
        "Policy dialogues",
        "Expert-led presentations",
        "Student and youth engagement"
      ],
      targetAudience: [
        "Public health professionals and researchers",
        "Health policymakers and government officials",
        "Academic institutions and students",
        "International health organizations"
      ],
      outcomes: [
        "Policy-relevant insights",
        "Knowledge exchange across sectors",
        "Stronger public health networks"
      ],
      icon: "🌍",
    },
    "future-leaders": {
      id: 6,
      title: "ClinAddis Future Leaders",
      partner: "High Schools",
      description: "A youth-focused program designed to introduce high school students to healthcare careers, innovation, and community health leadership at an early stage.",
      detailedDescription: `
        ClinAddis Future Leaders is a youth-focused program designed to introduce high school
        students to healthcare careers, innovation, and community health leadership at an early stage.
      `,
      whyItMatters: `
        Early exposure shapes future career paths. This program helps students understand healthcare
        beyond textbooks and inspires them to pursue meaningful roles in health and science.
      `,
      whatStudentsExperience: [
        "Introductory sessions on healthcare careers",
        "Mentorship from health professionals",
        "Community health activities",
        "Innovation and problem-solving challenges"
      ],
      targetAudience: [
        "High school students interested in health sciences",
        "Schools seeking enrichment programs"
      ],
      impact: [
        "Increased interest in healthcare careers",
        "Early leadership and civic engagement",
        "Stronger pipeline of future health professionals"
      ],
      icon: "🎓",
    }
    // Add remaining programs similarly...
  };

  const program = programs[slug];

  if (!program) {
    return (
      <div className="program-detail-page">
        <div className="container">
          <div className="not-found">
            <h2>Program Not Found</h2>
            <p>The program you're looking for doesn't exist or has been moved.</p>
            <Link to="/programs" className="btn btn-primary">
              Back to Programs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="program-detail-page">
      {/* Back Navigation */}
      <div className="container">
        <Link to="/programs" className="back-navigation">
          <span>Back to All Programs</span>
        </Link>
      </div>

      {/* Hero Section */}
      <section className="program-hero">
        <div className="container">
          <div className="program-hero-content">
            <div className="program-icon-large">{program.icon}</div>
            <div>
              <h1>{program.title}</h1>
              <div className="program-partner-large">
                <span className="partner-label">In partnership with:</span>
                <span className="partner-name">{program.partner}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section">
        <div className="container">
          <div className="program-detail-grid">
            {/* Left Column - Main Content */}
            <div className="program-main-content">
              {/* Overview */}
              <div className="program-section">
                <h2>Overview</h2>
                <p className="program-description-detail">
                  {program.detailedDescription || program.description}
                </p>
              </div>

              {/* Why It Matters */}
              {program.whyItMatters && (
                <div className="program-section">
                  <h2>Why This Program Matters</h2>
                  <p className="program-description-detail">{program.whyItMatters}</p>
                </div>
              )}

              {/* What Students Experience - For Future Leaders */}
              {program.whatStudentsExperience && (
                <div className="program-section">
                  <h2>What Students Experience</h2>
                  <ul className="program-list">
                    {program.whatStudentsExperience.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Focus Areas - For Public Health Forum */}
              {program.focusAreas && (
                <div className="program-section">
                  <h2>Focus Areas</h2>
                  <ul className="program-list">
                    {program.focusAreas.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Format - For Public Health Forum */}
              {program.format && (
                <div className="program-section">
                  <h2>Format</h2>
                  <ul className="program-list">
                    {program.format.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What Happens - For Community Connect */}
              {program.whatHappens && (
                <div className="program-section">
                  <h2>What Happens in Community Connect</h2>
                  <ul className="program-list">
                    {program.whatHappens.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Program Elements - For Art & Medicine */}
              {program.programElements && (
                <div className="program-section">
                  <h2>Program Elements</h2>
                  <ul className="program-list">
                    {program.programElements.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Core Focus Areas - For Skills & Leadership Program */}
              {program.coreFocusAreas && (
                <div className="program-section">
                  <h2>Core Focus Areas</h2>
                  <ul className="program-list">
                    {program.coreFocusAreas.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What We Do - For Innovation Lab */}
              {program.whatWeDo && !program.whatHappens && !program.whatStudentsExperience && (
                <div className="program-section">
                  <h2>What We Do</h2>
                  <ul className="program-list">
                    {program.whatWeDo.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Learning Approach - For Skills & Leadership Program */}
              {program.learningApproach && (
                <div className="program-section">
                  <h2>Learning Approach</h2>
                  <ul className="program-list">
                    {program.learningApproach.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Target Audience */}
              {program.targetAudience && (
                <div className="program-section">
                  <h2>Who It's For</h2>
                  <div className="audience-tags">
                    {program.targetAudience.map((audience, index) => (
                      <span key={index} className="audience-tag">
                        {audience}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Impact - For Art & Medicine and Future Leaders */}
              {program.impact && (
                <div className="program-section">
                  <h2>Impact</h2>
                  <ul className="program-list">
                    {program.impact.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Outcomes - For Innovation Lab, Skills & Leadership, Community Connect, and Public Health Forum */}
              {program.outcomes && !program.impact && (
                <div className="program-section">
                  <h2>Outcomes</h2>
                  <ul className="program-list">
                    {program.outcomes.map((outcome, index) => (
                      <li key={index}>{outcome}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Objectives - For programs that use objectives instead of outcomes/impact */}
              {program.objectives && !program.outcomes && !program.impact && (
                <div className="program-section">
                  <h2>Program Objectives</h2>
                  <ul className="program-list">
                    {program.objectives.map((objective, index) => (
                      <li key={index}>{objective}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramDetail;